/**
 * useConceptValues Hook
 * Manages concept values fetching with search functionality
 */

import { useState, useCallback, useEffect } from 'react';
import { apiClient } from '../services/apiClient';
import type { ConceptValue } from '../types';

interface UseConceptValuesResult {
  values: string[];
  loading: boolean;
  search: (searchText: string) => void;
  reload: () => void;
}

/**
 * Hook for fetching and managing concept values
 * @param conceptName - Name of the concept (e.g., 'ProductType')
 * @param initialLoad - Whether to load values on mount (default: true)
 * @returns Object containing values, loading state, and search function
 */
export function useConceptValues(
  conceptName: string,
  initialLoad: boolean = true
): UseConceptValuesResult {
  const [values, setValues] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchValues = useCallback(
    async (searchText?: string) => {
      console.log(`🔍 [useConceptValues] fetchValues called for ${conceptName}, search: "${searchText || 'none'}"`);
      setLoading(true);
      try {
        const params: Record<string, string> = {
          concept_name: conceptName,
        };
        if (searchText && searchText.trim()) {
          params.name = searchText.trim();
        }

        console.log(`🌐 [useConceptValues] API call START for ${conceptName}`);
        const result = await apiClient.get<ConceptValue[]>(
          '/knowledge_manager/concepts/values',
          params
        );

        console.log(
          `✅ [useConceptValues] API call END for ${conceptName}${
            searchText ? ` (search: ${searchText})` : ''
          }:`,
          result
        );

        if (result.status === 'success' && Array.isArray(result.data)) {
          setValues(result.data.map((item: ConceptValue) => item.name));
        } else {
          setValues([]);
        }
      } catch (error) {
        console.error(
          `❌ [useConceptValues] Error fetching values for ${conceptName}:`,
          error
        );
        setValues([]);
      } finally {
        setLoading(false);
      }
    },
    [conceptName]
  );

  const search = useCallback(
    (searchText: string) => {
      fetchValues(searchText);
    },
    [fetchValues]
  );

  const reload = useCallback(() => {
    console.log(`🔄 [useConceptValues] reload() called for ${conceptName}`);
    fetchValues();
  }, [fetchValues, conceptName]);

  // Initial load
  useEffect(() => {
    if (initialLoad) {
      fetchValues();
    }
  }, [initialLoad, fetchValues]);

  return {
    values,
    loading,
    search,
    reload,
  };
}

